import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import { generateReduxKeys } from '../../utils';
import { formatOrderItems } from '../../utils/formatOrderItems';

// The Catalogue lists the order's items with tiers in the shape of its own price list (`unit_price`);
// the order table reads `tier_price`.
const withOrderTiers = (item) =>
    Array.isArray(item?.tiers)
        ? { ...item, tiers: item.tiers.map((tier) => ({ ...tier, tier_price: tier.tier_price ?? tier.unit_price })) }
        : item;

const isPlaceholder = (item) => typeof item?.id === 'string' && item.id.startsWith('pending-');

const initialState = generateReduxKeys({
    orders: [],
    openOrderId: null,
    orderItems: [],
});

const orderSlice = createSlice({
    name: 'order',
    initialState,
    reducers: {
        setOrders: (state, action) => {
            state.orders = action.payload;
        },
        setOrderItems: (state, action) => {
            state.orderItems = formatOrderItems(action.payload);
        },
        appendOrderItems: (state, action) => {
            const prevOrderItems = Array.isArray(state.orderItems) ? state.orderItems : [];

            const newOrders = Array.isArray(action.payload)
                ? action.payload
                : [action.payload];

            const merged = _.cloneDeep(prevOrderItems);

            newOrders.forEach((newOrder) => {
                const existing = _.find(merged, { product_netsuite_id: newOrder.product_netsuite_id });

                if (existing) {
                    existing.quantity = Number(newOrder.quantity) || 1;
                } else {
                    merged.push(newOrder);
                }
            });

            state.orderItems = formatOrderItems(merged);
        },
        // The Catalogue also reports the order's items. Rows the order page already has are richer than those
        // (units, preferred flag, tiers), so they are kept and only take the saved quantity, unit, tier and price.
        mergeOrderItems: (state, action) => {
            const incoming = Array.isArray(action.payload) ? action.payload : [];
            const known = new Map(state.orderItems.map((item) => [item.id, item]));

            const merged = incoming.map((item) => {
                const existing = known.get(item.id);
                if (!existing) return withOrderTiers(item);
                const { quantity, unit_price, uom, price_level } = item;
                return { ...existing, quantity, unit_price, uom, price_level };
            });

            state.orderItems = [...formatOrderItems(merged), ...state.orderItems.filter(isPlaceholder)];
        },
        // Puts one item, as the server returned it (its rows per tier), in place of the one it replaces or at the end.
        upsertOrderItem: (state, action) => {
            const [item] = formatOrderItems(action.payload);
            if (!item) return;

            const index = state.orderItems.findIndex(
                (existing) => existing.id === item.id || existing.product_netsuite_id === item.product_netsuite_id
            );
            if (index >= 0) state.orderItems[index] = item;
            else state.orderItems.push(item);
        },
        updateOrderItemFields: (state, action) => {
            const { id, changes } = action.payload;
            const item = state.orderItems.find((existing) => existing.id === id);
            if (item) Object.assign(item, changes);
        },
        removeOrderItemById: (state, action) => {
            const updatedOrders = state.orderItems.filter((order) => order.id !== action.payload);
            state.orderItems = updatedOrders;
        },
        reinsertOrderItem: (state, action) => {
            const { item, index } = action.payload;
            if (state.orderItems.some((existing) => existing.id === item.id)) return;
            state.orderItems.splice(Math.min(index, state.orderItems.length), 0, item);
        },
        setOpenOrderId: (state, action) => {
            state.openOrderId = action.payload;
        },
    },
});

export const {
    setOrders,
    setOrderItems,
    appendOrderItems,
    mergeOrderItems,
    upsertOrderItem,
    updateOrderItemFields,
    removeOrderItemById,
    reinsertOrderItem,
    setOpenOrderId,
} = orderSlice.actions;

export default orderSlice.reducer;
